import { Box, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import FileUploadIcon from '../../../../components/Icons/FileUploadIcon';
import { colors, themePalette } from '../../../../components/ThemeProvider/themeColors';

interface UploadAreaProps {
  acceptedExtensions: Record<string, string[]>;
  maxSizeMB: number;
  onFileSelect?: (file: File) => void;
}

const UploadArea = ({ acceptedExtensions, maxSizeMB, onFileSelect }: UploadAreaProps) => {
  const { t } = useTranslation('salary-card');
  const [error, setError] = useState<string | null>(null);
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      setError(null);

      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        const code = rejection.errors?.[0]?.code;

        if (code === 'file-too-large') {
          setError(t('upload.errors.fileTooLarge', { maxSizeMB }));
        } else if (code === 'file-invalid-type') {
          setError(t('upload.errors.fileInvalidType'));
        }

        return;
      }

      if (acceptedFiles.length > 0) {
        onFileSelect?.(acceptedFiles[0]);
      }
    },
    [maxSizeMB, onFileSelect]
  );

  const extensions = Object.values(acceptedExtensions).flat();
  const extensionLabel = extensions.map(ext => ext.replace('.', '').toLowerCase()).join(', ');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    maxSize: maxSizeBytes,
    accept: acceptedExtensions,
  });

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        {...getRootProps()}
        sx={{
          width: '100%',
          height: '140px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          border: `1px dashed ${isDragActive ? colors.info[600] : colors.base[300]}`,
          borderRadius: '8px',
          backgroundColor: isDragActive ? colors.info[50] : colors.base[50],
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: colors.info[600],
            backgroundColor: colors.info[50],
          },
        }}
      >
        <input {...getInputProps()} />
        <FileUploadIcon sx={{ width: '32px', height: '32px' }} />
        <Typography fontSize={14} fontWeight={500} color={themePalette.text_and_icon.secondary}>
          {isDragActive ? t('upload.dropActive') : t('upload.dropIdle')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
        <Typography
          display='flex'
          alignItems='center'
          gap={0.5}
          fontSize={12}
          fontWeight={400}
          color={colors.base[500]}
        >
          {t('upload.allowedFormats')}{' '}
          <Typography fontSize={12} fontWeight={400}>
            {extensionLabel}.
          </Typography>
        </Typography>
        <Typography
          display='flex'
          alignItems='center'
          gap={0.5}
          fontSize={12}
          fontWeight={400}
          color={colors.base[500]}
        >
          {t('upload.maxSize')}{' '}
          <Typography fontSize={12} fontWeight={400}>
            {maxSizeMB} MB
          </Typography>
        </Typography>
        {error && (
          <Typography fontSize={12} fontWeight={500} color={colors.error[700]}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default UploadArea;
